import { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Select,
  Flex,
  List,
  ListItem,
  useToast,
  SystemStyleObject,
} from '@chakra-ui/react';
import { getPlaceSuggestions, getPlaceDetails } from '../services/flaskService';
import debounce from 'lodash/debounce';

export interface AddressFormData {
  address: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  secondaryAddressType: string;
  secondaryAddressNumber: string;
  isAddressValidated: boolean;
}

interface AddressFormFieldsProps {
  initialAddress?: string;
  initialStreetAddress?: string;
  initialCity?: string;
  initialState?: string;
  initialZip?: string;
  initialCountry?: string;
  initialSecondaryAddressType?: string;
  initialSecondaryAddressNumber?: string;
  initialIsAddressValidated?: boolean;
  onAddressChange?: (data: AddressFormData) => void;
  showLabel?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  borderColor?: string;
  _hover?: SystemStyleObject;
  _focus?: SystemStyleObject;
}

/** Lime board input: 48px, radius 14, 1px #DCDCD8, 16px text. Shared with the shipping form. */
export const boardInputProps = {
  h: '48px',
  borderRadius: '14px',
  fontSize: '16px',
  bg: '#FFFFFF',
  color: '#0E0E0E',
  _placeholder: { color: '#8A8A8E' },
} as const;
export const boardInputBorder = {
  borderColor: '#DCDCD8',
  _hover: { borderColor: '#B4B4B8' },
  _focus: { borderColor: '#0E0E0E', boxShadow: '0 0 0 1px #0E0E0E' },
} as const;
/** Field label: 14px / 700 ink. */
export const boardLabelProps = { fontSize: '14px', fontWeight: 600, color: '#0E0E0E', mb: '6px' } as const;

const AddressFormFields: React.FC<AddressFormFieldsProps> = ({
  initialAddress = '',
  initialStreetAddress = '',
  initialCity = '',
  initialState = '',
  initialZip = '',
  initialCountry = '',
  initialSecondaryAddressType = '',
  initialSecondaryAddressNumber = '',
  initialIsAddressValidated = false,
  onAddressChange,
  showLabel = true,
  inputSize = 'md',
  borderColor = boardInputBorder.borderColor,
  _hover = boardInputBorder._hover,
  _focus = boardInputBorder._focus,
}) => {
  const toast = useToast();

  const [isAddressValidated, setIsAddressValidated] = useState(initialIsAddressValidated);
  const [address, setAddress] = useState(initialAddress);
  const [secondaryAddressNumber, setSecondaryAddressNumber] = useState(initialSecondaryAddressNumber);
  const [secondaryAddressType, setSecondaryAddressType] = useState(initialSecondaryAddressType);
  const [streetAddress, setStreetAddress] = useState(initialStreetAddress);
  const [city, setCity] = useState(initialCity);
  const [state, setState] = useState(initialState);
  const [zip, setZip] = useState(initialZip);
  const [country, setCountry] = useState(initialCountry);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Update state when initial values change (for editing scenarios)
  useEffect(() => {
    setAddress(initialAddress);
    setStreetAddress(initialStreetAddress);
    setCity(initialCity);
    setState(initialState);
    setZip(initialZip);
    setCountry(initialCountry);
    setSecondaryAddressType(initialSecondaryAddressType);
    setSecondaryAddressNumber(initialSecondaryAddressNumber);
    setIsAddressValidated(initialIsAddressValidated);
  }, [
    initialAddress,
    initialStreetAddress,
    initialCity,
    initialState,
    initialZip,
    initialCountry,
    initialSecondaryAddressType,
    initialSecondaryAddressNumber,
    initialIsAddressValidated,
  ]);

  // Notify parent of changes
  useEffect(() => {
    if (onAddressChange) {
      onAddressChange({
        address,
        streetAddress,
        city,
        state,
        zip,
        country,
        secondaryAddressType,
        secondaryAddressNumber,
        isAddressValidated,
      });
    }
  }, [
    address,
    streetAddress,
    city,
    state,
    zip,
    country,
    secondaryAddressType,
    secondaryAddressNumber,
    isAddressValidated,
    onAddressChange,
  ]);

  const fetchSuggestions = async (input: string) => {
    if (input.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await getPlaceSuggestions(input);
      if (results.length === 0) {
        toast({
          title: 'No suggestions found',
          description: 'Please try a different address',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const debouncedFetchSuggestions = useCallback(debounce(fetchSuggestions, 300), []);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    debouncedFetchSuggestions(value);
    setIsAddressValidated(false);
  };

  const handleSuggestionClick = async (suggestion: any) => {
    try {
      const placeDetails = await getPlaceDetails(suggestion.placeId);
      if (!placeDetails) {
        toast({
          title: 'Error',
          description: 'Failed to get address details. Please try again.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const { streetNumber, streetName, city, state, zip, country } = placeDetails;
      setAddress(`${streetNumber} ${streetName}, ${city}, ${state}, ${zip}`);
      setStreetAddress(`${streetNumber} ${streetName}`);
      setCity(city);
      setState(state);
      setZip(zip);
      setCountry(country);
      setSuggestions([]);
      setShowSuggestions(false);
      setIsAddressValidated(true);
    } catch (error) {
      console.error('Error handling suggestion click:', error);
      toast({
        title: 'Error',
        description: 'Failed to process address selection. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <FormControl isRequired position="relative">
        {showLabel && <FormLabel {...boardLabelProps}>Address</FormLabel>}
        <Input
          value={address}
          onChange={handleAddressChange}
          placeholder="Start typing your address..."
          size={inputSize}
          {...boardInputProps}
          borderColor={borderColor}
          _hover={_hover}
          _focus={_focus}
        />
        {showSuggestions && suggestions.length > 0 && (
          <List
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="#FFFFFF"
            border="1px solid #DCDCD8"
            boxShadow="0 8px 24px rgba(14,14,14,0.12)"
            borderRadius="14px"
            maxH="200px"
            overflowY="auto"
            zIndex={1000}
            mt={1}
          >
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={index}
                px="14px"
                py="12px"
                cursor="pointer"
                _hover={{ bg: '#F2F2F0' }}
                onClick={() => handleSuggestionClick(suggestion)}
                fontSize="15px"
                color="#0E0E0E"
              >
                {suggestion.description}
              </ListItem>
            ))}
          </List>
        )}
      </FormControl>
      <FormControl>
        {showLabel && <FormLabel {...boardLabelProps}>Secondary Address Line</FormLabel>}
        <Flex gap={2}>
          <Select
            placeholder="Select type"
            flex={1}
            size={inputSize}
            {...boardInputProps}
            borderColor={borderColor}
            _hover={_hover}
            _focus={_focus}
            value={secondaryAddressType}
            onChange={(e) => setSecondaryAddressType(e.target.value)}
          >
            <option value="Apt">Apt</option>
            <option value="Rm">Rm</option>
            <option value="Unit">Unit</option>
            <option value="Building">Building</option>
            <option value="PO Box">PO Box</option>
          </Select>
          <Input
            flex={1}
            value={secondaryAddressNumber}
            onChange={(e) => setSecondaryAddressNumber(e.target.value)}
            placeholder="Number/Letter"
            size={inputSize}
            {...boardInputProps}
            borderColor={borderColor}
            _hover={_hover}
            _focus={_focus}
          />
        </Flex>
      </FormControl>
    </>
  );
};

export default AddressFormFields;
